import { usePermissions } from '@/hooks/usePermissions'
import { Alert, Badge, Group, Text } from '@mantine/core'
import { IconEyeOff, IconInfoCircle, IconLock, IconShield } from '@tabler/icons-react'

interface RoleInfoBannerProps {
  showPIIMaskedWarning?: boolean
  showExportLimitWarning?: boolean
}

/**
 * Component that displays role-specific information and warnings
 */
export function RoleInfoBanner({ 
  showPIIMaskedWarning = false,
  showExportLimitWarning = false
}: RoleInfoBannerProps) {
  const { userRole, isAdmin, canViewPII, canExportFullData } = usePermissions()

  // Don't show anything for admins
  if (isAdmin) return null

  const showWarning = (showPIIMaskedWarning && !canViewPII) || (showExportLimitWarning && !canExportFullData)

  if (!showWarning) return null

  return (
    <Alert 
      icon={<IconInfoCircle size={16} />}
      title="Role-Based Data Access"
      color="blue"
      variant="light"
      mb="md"
    >
      <Group gap="xs">
        <Badge size="sm" color="blue" variant="light">
          {userRole === 'Public-Records-Expert' ? 'Public Records Expert' : userRole}
        </Badge>
      </Group>

      <Text size="sm" mt="xs">
        {!canViewPII && showPIIMaskedWarning && (
          <Group gap="xs" mb="xs">
            <IconEyeOff size={14} />
            <span>PII data (SSN, full DOB) is masked for your role</span>
          </Group>
        )}
        {!canExportFullData && showExportLimitWarning && (
          <Group gap="xs">
            <IconLock size={14} />
            <span>Export limited to filtered data only</span>
          </Group>
        )}
      </Text>
    </Alert>
  )
}

/**
 * Inline badge to show when data is masked
 */
export function MaskedDataBadge() {
  return (
    <Badge 
      size="xs" 
      color="gray" 
      variant="dot"
      leftSection={<IconEyeOff size={10} />}
    >
      Masked
    </Badge>
  )
}

/**
 * Badge showing admin-only feature
 */
export function AdminOnlyBadge() {
  return (
    <Badge 
      size="xs" 
      color="red" 
      variant="light"
      leftSection={<IconShield size={10} />}
    >
      Admin Only
    </Badge>
  )
}
